import React from 'react'
import moment from 'moment-with-locales-es6'
import HistoryMethods, { IHistoryItem, IInstance } from './history.local.methods'
import HistoryHeader from './__header/history__header'
import Content from 'blocks.app/content/content'
import LoaderLazy from 'blocks.app/loader/_lazy/loader__lazy'
import InfiniteScroll from 'react-infinite-scroller'
import Icon from 'blocks.simple/icon/icon'
import translate from 'core/translate'
import helpers from 'core/helpers'
import { styles } from './history-styles'
import { checkAvailableModule } from '../../core/helpers/routes/routes'
import { DetailsModal } from './__detailsModal/DetailsModal'

const renderEntries = (entry: IHistoryItem) =>
    entry.context.instances
        .map((instance: IInstance) => {
            if (!instance) {
                return null
            }

            if (instance && instance.broadcast) {
                let endDate = null
                const startDate = helpers.getLocalDate({ date: instance.startDate, time: instance.startTime })
                const formatStartDate = startDate ? startDate.format('YYYY/MM/DD HH:mm:ss') : null
                let formatEndDate = null

                if (instance.endDate) {
                    endDate = helpers.getLocalDate({ date: instance.endDate, time: instance.endTime })
                    formatEndDate = endDate ? endDate.format('YYYY/MM/DD HH:mm:ss') : null
                } else {
                    formatEndDate = translate('forever')
                }
                return `(${formatStartDate} - ${formatEndDate}) ${instance.broadcast.title}`
            }

            if (instance.label) {
                return instance.label
            }

            if (instance.name) {
                return instance.name
            }

            return instance.title
        })
        .join(', ')

const History = (p_: {
    getKeepDurationSettings: (field: string, defaultValue: string) => string
    location: Location
    goToSettings: () => void
}) => {
    const {
        historyList,
        hasMore,
        isLoading,
        _parsedQuery,
        getHistory,
        detailsHistoryEventId,
        onDetailsModalOpen,
        onDetailsModalClose,
        withUserTimezone,
        changeWithUserTimezone,
    } = HistoryMethods(p_)

    return (
        <Content title="eventJournal" wrapperClassName={styles.historyWrapper}>
            <div className={styles.caption}>
                {translate('messageKeepDuration')}: {p_.getKeepDurationSettings('userActionHistoryLifespanD', '180')}
                <div className={styles.changeSettings} onClick={p_.goToSettings}>
                    {translate('editLabel')}
                </div>
            </div>
            <HistoryHeader
                location={p_.location}
                query={_parsedQuery}
                withUserTimezone={withUserTimezone}
                changeWithUserTimezone={changeWithUserTimezone}
            />
            {!!historyList.length && (
                <InfiniteScroll
                    loadMore={() => getHistory(true)}
                    hasMore={hasMore}
                    initialLoad={false}
                    loader={<LoaderLazy key="history__loader" active={isLoading && historyList.length > 0} />}
                    threshold={10}
                    useWindow={true}
                >
                    <div className={styles.table}>
                        <div className={styles.tableHeader} role="row">
                            <div role="columnheader">{translate('session')}</div>
                            <div role="columnheader">{translate('eventStatus')}</div>
                            <div role="columnheader">{translate('user')}</div>
                            <div role="columnheader">{translate('event')}</div>
                            <div role="columnheader" />
                        </div>
                        {historyList.map((entry: IHistoryItem) => (
                            <div className={styles.tableRow} key={entry.id} role="row">
                                <div className={styles.dateCell} role="cell">
                                    <span>{moment(entry.createdAt).format('DD.MM.YYYY')}</span>
                                    <span>
                                        {withUserTimezone
                                            ? moment(entry.createdAt).format('HH:mm:ss')
                                            : moment(entry.createdAt).utc().format('HH:mm:ss')}
                                    </span>
                                </div>
                                <div className={styles.statusCell} role="cell">
                                    {translate('information')}
                                </div>
                                <div className={styles.userCell} role="cell">
                                    <span>
                                        {entry.user.roles && entry.user.roles.length
                                            ? helpers.getTranslatedIfRole(entry.user.roles[0])
                                            : ''}
                                    </span>
                                    <span>{entry.user.email}</span>
                                </div>
                                <div className={styles.eventCell} role="cell">
                                    <span>{translate(`${entry.action}_${entry.object}`)}</span>
                                    <span>{renderEntries(entry)}</span>
                                </div>
                                {entry.withDetail ? (
                                    <button
                                        type="button"
                                        className={styles.detailsButton}
                                        aria-label={translate('showDetails')}
                                        onClick={() => onDetailsModalOpen(entry.id)}
                                    >
                                        <Icon type={'arrow_right'} size={16} />
                                    </button>
                                ) : (
                                    <span />
                                )}
                            </div>
                        ))}
                    </div>
                </InfiniteScroll>
            )}
            {!isLoading && !historyList.length && (
                <div className={styles.emptyState}>
                    <Icon type={'vision'} size={40} />
                    <div>{translate('noNewEvents')}</div>
                </div>
            )}
            {detailsHistoryEventId && <DetailsModal id={detailsHistoryEventId} onClose={onDetailsModalClose} />}
        </Content>
    )
}

const exportHistory: any = checkAvailableModule('history') ? History : null

export default exportHistory
